import React, { useRef, useMemo, useEffect, useCallback, useState } from 'react';
import { pdfjs } from 'react-pdf';
import usePdfState from '../hooks/usePdfState';
import useSelectionManager from '../hooks/useSelectionManager';
import { useHighlightingSystem } from '../hooks/useHighlightingSystem';
import { useCommentSystem } from '../hooks/useCommentSystem';
import { useNotesSystem } from '../hooks/useNotesSystem';
import chatConfigService from './services/chatConfigService';

import PdfViewerLayout from './PdfViewerLayout';
import PdfContentArea from './PdfContentArea';
import SidebarControls from './SidebarControls';
import NotesBox from './NotesSystem/NotesBox';
import ChatBox from './ChatSystem/ChatBox';

import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import "../styles/main.css";
import "../styles/pdf-container.css";
import "../styles/sidebar.css";

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// Create a memoized version of SidebarControls
const MemoizedSidebarControls = React.memo(SidebarControls);

export default function PdfViewer({ documentTitle, onBackToDashboard }) {
  const containerRef = useRef(null);
  const pdfScrollContainerRef = useRef(null);
  const contentAreaRef = useRef(null); // NEW: Ref for the PDF content area

  // Debug render tracking
  const renderCount = useRef(0);
  const prevProps = useRef({ documentTitle });

  // Track what changed between renders
  useEffect(() => {
    renderCount.current += 1;
    prevProps.current = { documentTitle };
  });

  const {
    filePath, numPages, pageNumber, zoom,
    highlightMode, commentMode, selectedText, highlights, comments,
    setFilePath, setNumPages, setPageNumber, setZoom,
    setHighlightMode, setCommentMode,
    setSelectedText, setSelectionRects, setHighlights, setComments,
    resetState
  } = usePdfState();

  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [notesMode, setNotesMode] = useState(false);
  const [isNotesVisible, setIsNotesVisible] = useState(false);
  const [chatboxMode, setChatboxMode] = useState(false);
  const [hasSentWelcome, setHasSentWelcome] = useState(false);
  const [documentKey, setDocumentKey] = useState(0);
  const [contentAreaBounds, setContentAreaBounds] = useState(null); // NEW: State for content area bounds

  // Update content area bounds when sidebar visibility changes or component mounts
  useEffect(() => {
    const updateBounds = () => {
      if (contentAreaRef.current) {
        const rect = contentAreaRef.current.getBoundingClientRect();
        setContentAreaBounds({
          left: rect.left,
          top: rect.top,
          width: rect.width,
          height: rect.height
        });
      }
    };

    // Initial update
    updateBounds();

    // Update on resize and sidebar toggle
    window.addEventListener('resize', updateBounds);
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', updateBounds);
    };
  }, [isSidebarVisible]); // Re-run when sidebar visibility changes

  // Update bounds when notes visibility changes (NotesBox might affect layout)
  useEffect(() => {
    if (contentAreaRef.current) {
      const rect = contentAreaRef.current.getBoundingClientRect();
      setContentAreaBounds({
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height
      });
    }
  }, [isNotesVisible, chatboxMode]); // Re-run when NotesBox or ChatBox visibility changes

  // Initialize filePath when documentTitle is provided
  useEffect(() => {
    if (documentTitle && !filePath) {
      setFilePath(documentTitle);
    }
  }, [documentTitle, filePath, setFilePath]);

  // RESET EVERYTHING WHEN DOCUMENT CHANGES
  useEffect(() => { 
    if (documentTitle && documentTitle !== filePath) {
      resetState(documentTitle);
      setNotesMode(false);
      setIsNotesVisible(false);
      setChatboxMode(false);
      setHasSentWelcome(false);
      setHighlightMode(false);
      setCommentMode(false);
      setContentAreaBounds(null); // NEW: Reset bounds
      window.getSelection()?.removeAllRanges();
      
      // Clear storage
      const previousPdfTitle = filePath;
      if (previousPdfTitle) {
        sessionStorage.removeItem(`chatbox_position_${previousPdfTitle}`);
        sessionStorage.removeItem(`chatbox_size_${previousPdfTitle}`);
        sessionStorage.removeItem(`chat_session_${previousPdfTitle}`);
        sessionStorage.removeItem(`notes_position_${previousPdfTitle}`);
        sessionStorage.removeItem(`notes_size_${previousPdfTitle}`);
      }
      sessionStorage.removeItem('notesbox_position');
      sessionStorage.removeItem('notesbox_size');
      
      setDocumentKey(prev => prev + 1);
    }
  }, [documentTitle, filePath, resetState]);

  // KEYBOARD NAVIGATION
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') return;

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        handlePreviousPage();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        handleNextPage();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [pageNumber, numPages]);

  const handlePreviousPage = useCallback(() => {
    if (pageNumber > 1) setPageNumber(pageNumber - 1);
  }, [pageNumber, setPageNumber]);

  const handleNextPage = useCallback(() => {
    if (pageNumber < numPages) setPageNumber(pageNumber + 1);
  }, [pageNumber, numPages, setPageNumber]);

  // Text selection management
  const selection = useSelectionManager(pdfScrollContainerRef, zoom, documentKey, highlights);
  
  const memoizedSelection = useMemo(() => ({
    selectedText: selection.selectedText,
    selectionRects: selection.selectionRects,
    selectionContext: selection.selectionContext,
    hasHighlightOnSelection: selection.hasHighlightOnSelection,
    matchingHighlightIds: selection.matchingHighlightIds
  }), [selection.selectedText, selection.selectionRects, selection.selectionContext, selection.hasHighlightOnSelection, selection.matchingHighlightIds]);

  useEffect(() => {
    setSelectedText(memoizedSelection.selectedText);
    setSelectionRects(memoizedSelection.selectionRects);
  }, [memoizedSelection.selectedText, memoizedSelection.selectionRects, setSelectedText, setSelectionRects]);

  // Highlighting system
  const handleHighlightsChange = useCallback((newHighlights) => {
    setHighlights(newHighlights);
  }, [setHighlights]);

  const highlighting = useHighlightingSystem(
    pdfScrollContainerRef, pageNumber, memoizedSelection.selectionRects,
    highlightMode, highlights, handleHighlightsChange, filePath, documentKey
  );

  // Commenting system
  const handleCommentsChange = useCallback((newComments) => {
    setComments(newComments);
  }, [setComments]);

  const commenting = useCommentSystem(
    pdfScrollContainerRef, pageNumber, zoom, commentMode,
    comments, handleCommentsChange, filePath, documentKey
  );

  // Notes system
  const notes = useNotesSystem(filePath, documentKey);

  // Helper function to dispatch context data
  const dispatchContextToChat = useCallback((fullContextData) => {
    const event = new CustomEvent('sendToChat', { 
      detail: { 
        contextData: fullContextData,
        timestamp: new Date().toISOString(),
        source: 'pdf-selection',
        structured: true,
        document: filePath
      }
    });
    console.log('🎯 Dispatching sendToChat event:', filePath);
    window.dispatchEvent(event);
  }, [filePath]);

  // -----------------------------------------------------------
  // ✅ LOGIC: OPEN CHATBOX IMMEDIATELY IF CLOSED
  // -----------------------------------------------------------
  const handleSendToChat = useCallback((contextData) => {
    console.log('🔍 DEBUG - handleSendToChat received:', contextData);
    
    // Prepare data
    let fullContextData;
    if (typeof contextData === 'string') {
      fullContextData = { selectedText: contextData, contextBefore: '', contextAfter: '' };
    } else {
      fullContextData = {
        selectedText: contextData.selectedText || '',
        contextBefore: contextData.contextBefore || '',
        contextAfter: contextData.contextAfter || ''
      };
    }
    
    // Check if chatbox is currently closed
    if (!chatboxMode) {
      console.log('💬 Chatbox is closed. Opening immediately...');
      setChatboxMode(true);
      
      // We need a small delay to allow the ChatBox component to mount 
      // and attach its event listeners before we fire the event.
      // Increased to 300ms to be safe.
      setTimeout(() => {
        console.log('📤 Dispatching context data (delayed)...');
        dispatchContextToChat(fullContextData);
      }, 300);
    } else {
      console.log('📤 Chatbox already open, dispatching immediately...');
      dispatchContextToChat(fullContextData);
    }
    
    // Clear the selection UI immediately
    window.getSelection()?.removeAllRanges();
    setSelectedText("");
    setSelectionRects([]);
    
  }, [chatboxMode, setSelectedText, setSelectionRects, setChatboxMode, dispatchContextToChat]);

  // Welcome message handler
  const handlePdfNewlyRendered = useCallback(async () => {
    if (!filePath || filePath === "Untitled Document" || hasSentWelcome) return;

    try {
      const response = await chatConfigService.notifyPdfLoaded(filePath, 'pdf_render');
      
      const sendWelcome = () => {
        const welcomeMessage = {
          id: Date.now(),
          text: response.chatResponse || "Good luck reading/studying! 📚",
          sender: 'system',
          systemType: 'info',
          timestamp: new Date().toISOString(),
          source: 'pdf_welcome',
          document: filePath
        };
        
        window.dispatchEvent(new CustomEvent('systemMessage', {
          detail: { message: welcomeMessage, type: 'pdf_welcome', document: filePath }
        }));
        setHasSentWelcome(true);
      };

      if (!chatboxMode) {
        setChatboxMode(true);
        setTimeout(sendWelcome, 500);
      } else {
        sendWelcome();
      }
      
    } catch (error) {
      console.error('❌ Failed to get welcome message:', error);
      // Fallback
      window.dispatchEvent(new CustomEvent('systemMessage', {
        detail: { 
          message: {
            id: Date.now(),
            text: "Good luck reading/studying! 📚",
            sender: 'system',
            systemType: 'info',
            timestamp: new Date().toISOString()
          },
          type: 'pdf_welcome_fallback'
        }
      }));
      setHasSentWelcome(true);
    }
  }, [filePath, chatboxMode, hasSentWelcome, setChatboxMode]);

  // Remove multiple highlights
  const handleRemoveMultipleHighlights = useCallback((highlightIds) => {
    if (!highlightIds || highlightIds.length === 0) return;
    highlightIds.forEach(id => highlighting.deleteHighlight(id));
    window.getSelection()?.removeAllRanges();
  }, [highlighting.deleteHighlight]);

  // Toggle handlers
  const handleToggleNotesMode = useCallback(() => {
    const newNotesMode = !notesMode;
    setNotesMode(newNotesMode);
    setIsNotesVisible(newNotesMode);
    if (newNotesMode) {
      setHighlightMode(false);
      setCommentMode(false);
    }
  }, [notesMode, setHighlightMode, setCommentMode]);

  const handleToggleChatboxMode = useCallback(() => {
    const newChatboxMode = !chatboxMode;
    setChatboxMode(newChatboxMode);
    if (newChatboxMode) {
      setHighlightMode(false);
      setCommentMode(false);
    }
  }, [chatboxMode, setHighlightMode, setCommentMode]);

  const handleToggleCommentMode = useCallback(() => {
    const newCommentMode = !commentMode;
    setCommentMode(newCommentMode);
    if (newCommentMode) setHighlightMode(false);
  }, [commentMode, setHighlightMode]);

  const handleCloseNotes = useCallback(() => {
    setIsNotesVisible(false);
    setNotesMode(false);
  }, []);

  // Clear selection on mode change
  useEffect(() => { 
    window.getSelection()?.removeAllRanges(); 
  }, [pageNumber, highlightMode, commentMode, notesMode, chatboxMode]);

  // Device scale
  const deviceScale = useMemo(() => {
    const dpr = window.devicePixelRatio || 1;
    return dpr >= 2 ? dpr : dpr >= 1.5 ? 1.5 : 1.0;
  }, []);

  const onDocumentLoadSuccess = useCallback(({ numPages }) => {
    setNumPages(numPages);
  }, [setNumPages]);

  const toggleSidebar = useCallback(() => {
    setIsSidebarVisible(prev => !prev);
  }, []);

  // Notes actions
  const handleClearCurrentPageNotes = useCallback(() => {
    if (window.confirm('Are you sure you want to clear notes for the current page?')) {
      notes.updateNotes('');
    }
  }, [notes]);

  const handleClearAllNotes = useCallback(() => {
    if (window.confirm('Are you sure you want to clear ALL notes?')) {
      notes.clearNotes();
    }
  }, [notes]);

  const handleToggleHighlightMode = useCallback(() => {
    if (memoizedSelection.selectedText) {
      highlighting.addHighlight(memoizedSelection.selectedText);
      setHighlightMode(false);
    } else {
      setHighlightMode(prev => { 
        if (!prev) setCommentMode(false); 
        return !prev; 
      });
    }
  }, [memoizedSelection.selectedText, highlighting.addHighlight, setHighlightMode, setCommentMode]);

  const memoizedNotesStatus = useMemo(() => ({
    isLoading: notes.isLoading,
    isSaving: notes.isSaving,
    lastSaved: notes.lastSaved
  }), [notes.isLoading, notes.isSaving, notes.lastSaved]);

  return (
    <div id="app" ref={containerRef}>
      <PdfViewerLayout
        isSidebarVisible={isSidebarVisible}
        onToggleSidebar={toggleSidebar}
        onBackToDashboard={onBackToDashboard}

        sidebar={
          <MemoizedSidebarControls
            filePath={filePath}
            setFilePath={setFilePath}
            numPages={numPages}
            pageNumber={pageNumber}
            setPageNumber={setPageNumber}
            zoom={zoom}
            setZoom={setZoom}
            highlightMode={highlightMode}
            commentMode={commentMode}
            notesMode={notesMode}
            chatboxMode={chatboxMode}
            onPreviousPage={handlePreviousPage}
            onNextPage={handleNextPage}
            onToggleHighlightMode={handleToggleHighlightMode}
            onToggleCommentMode={handleToggleCommentMode}
            onToggleNotesMode={handleToggleNotesMode}
            onToggleChatboxMode={handleToggleChatboxMode}
            onClearAllHighlights={highlighting.clearHighlights}
            onClearCurrentPageHighlights={highlighting.clearCurrentPageHighlights}
            onRefreshHighlights={highlighting.refreshHighlights}
            onClearAllComments={commenting.clearComments}
            onClearCurrentPageComments={commenting.clearCurrentPageComments}
            onRefreshComments={commenting.refreshComments}
            onClearAllNotes={handleClearAllNotes}
            onClearCurrentPageNotes={handleClearCurrentPageNotes}
            isSidebarVisible={isSidebarVisible}
            highlightsCount={highlights.length}
            commentsCount={comments.length}
            isLoadingHighlights={highlighting.isLoading}
            isLoadingComments={commenting.isLoading}
            notesStatus={memoizedNotesStatus}
          />
        }
        mainContent={
          <>
            {/* Wrap PdfContentArea with a ref to get its bounds */}
            <div 
              ref={contentAreaRef} 
              style={{ 
                width: '100%', 
                height: '100%',
                position: 'relative'
              }}
            >
              <PdfContentArea
                filePath={filePath}
                pageNumber={pageNumber}
                zoom={zoom}
                deviceScale={deviceScale}
                onDocumentLoadSuccess={onDocumentLoadSuccess}
                pdfScrollContainerRef={pdfScrollContainerRef}
                highlights={highlighting.highlights}
                onDeleteHighlight={highlighting.deleteHighlight}
                onDeleteMultipleHighlights={highlighting.deleteMultipleHighlights}
                onAddHighlight={highlighting.addHighlight}
                comments={commenting.comments}
                activeComment={commenting.activeComment}
                tempComment={commenting.tempComment}
                onCommentClick={commenting.setActiveComment}
                onCommentMove={commenting.moveComment}
                onSaveComment={commenting.saveComment}
                onDeleteComment={commenting.deleteComment}
                onCancelComment={commenting.cancelComment}
                selectedText={selectedText}
                selectionRects={memoizedSelection.selectionRects}
                selectionContext={memoizedSelection.selectionContext}
                hasHighlightOnSelection={memoizedSelection.hasHighlightOnSelection}
                matchingHighlightIds={memoizedSelection.matchingHighlightIds}
                onSendToChat={handleSendToChat}
                onRemoveMultipleHighlights={handleRemoveMultipleHighlights}
                onPdfNewlyRendered={handlePdfNewlyRendered}
                commentMode={commentMode}
                onBackToDashboard={onBackToDashboard}
                key={documentKey}
              />
            </div>
            
            {isNotesVisible && (
              <NotesBox
                notes={notes.notes}
                onUpdateNotes={notes.updateNotes}
                onSaveNotes={notes.saveNotes}
                onDownloadNotes={notes.downloadNotes}
                onClose={handleCloseNotes}
                isSaving={notes.isSaving}
                lastSaved={notes.lastSaved}
                pdfTitle={filePath}
                containerBounds={contentAreaBounds} // NEW: Pass the container bounds
                key={`notes-${documentKey}`}
              />
            )}

            {chatboxMode && (
  <ChatBox 
    pdfTitle={filePath} 
    containerBounds={contentAreaBounds}
    currentPage={pageNumber} // Add this line
    key={`chat-${documentKey}`} 
  />
)}
          </>
        }
      />
    </div>
  );
}